import z from "zod";
import { useQuery, useMutation } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

type Options = {
    orderByDate?: boolean;
};

export const useGetAllCampaigns = (options?: Options) => {
    let url = "/api/campaigns";
    if (options?.orderByDate) {
        url = url.concat("?order[startDate]=asc");
    }
    const query = useQuery({
        queryKey: "getAllCampaigns",
        cacheTime: 15 * 60 * 1000, // 15 minutes in cache
        staleTime: 5 * 60 * 1000, // 5 minutes before considered stale
        refetchOnMount: true, // Refetch on mount but serve from cache during load
        refetchOnWindowFocus: false, // Don't refetch on window focus
        refetchOnReconnect: true, // Refetch when reconnecting to internet
        queryFn: async () => {
            return await loggedInApiCall.get(url);
        },
        select: (response) => {
            const campaigns = response.data["hydra:member"];
            const data = zodParseWithLog(campaigns, CampaignSchema.array(), "getAllCampaigns");
            return data;
        },
    });

    return {
        campaigns: query.data ?? [],
        ...query,
    };
};

export const useCreateCampaign = () => {
    const query = useMutation({
        mutationFn: async (campaign: CampaignWrite) => {
            const response =
                campaign.id === undefined ?
                    await loggedInApiCall.post("/api/campaigns", campaign)
                :   await loggedInApiCall.put("/api/campaigns/" + campaign.id, campaign);

            return response;
        },
    });

    return query;
};

export const useSetDefaultCampaign = () => {
    const query = useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.patch(
                "/api/campaigns/" + id,
                { active: true },
                { headers: { "Content-Type": "application/merge-patch+json" } },
            );
        },
    });

    return query;
};

export type CampaignUpdate = Omit<CampaignWrite, "active"> & { id: number };

export const useUpdateCampaign = () => {
    const query = useMutation({
        mutationFn: async ({ id, ...campaign }: CampaignUpdate) => {
            return await loggedInApiCall.patch("/api/campaigns/" + id, campaign, {
                headers: { "Content-Type": "application/merge-patch+json" },
            });
        },
    });

    return query;
};

export const useDeleteCampaign = () => {
    const query = useMutation({
        mutationFn: async (id: number) => {
            const response = await loggedInApiCall.delete("/api/campaigns/" + id);

            return response;
        },
    });

    return query;
};

export type Campaign = {
    "@id"?: string;
    id: number;
    label: string;
    year: number;
    startDate: string;
    endDate: string;
    active: boolean;
    isTarget: boolean;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};

export const CampaignSchema: z.ZodSchema<Campaign> = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    label: z.string(),
    year: z.number(),
    startDate: z.string(),
    endDate: z.string(),
    active: z.boolean(),
    isTarget: z.boolean(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export type CampaignWrite = {
    id?: number;
    label: string;
    year: number;
    startDate: string;
    endDate: string;
    active: boolean;
};

export const CampaignWriteSchema: z.ZodSchema<CampaignWrite> = z.object({
    id: z.number().optional(),
    label: z.string(),
    year: z.number(),
    startDate: z.string(),
    endDate: z.string(),
    active: z.boolean(),
});
